/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * FE_PTVang (ptvang@rsupartners.com)
 *
 * @description
 *
 * @created at Tue Jan 14 2025
 **/

import { useMemo, useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { TransformComponent, TransformWrapper, ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import { _T } from "@rfice/language";

import { Button, Image } from "@/frameworks/designStuff/components";
import { Utility } from "@/frameworks/libraries/utilities";
import { WrapperBtnChangeImg, styleImageViewer } from "./emotion";
import { LoadingFile, FileViewerMobileWrap } from "../../emotion";
import { MIN_SCALE_MOBILE, MAX_SCALE_MOBILE, DISTANCE_TO_SCREEN_EDGE } from "../constant";

import image1280x720 from "@/assets/images/loadingFailed/content.png";

interface ImageViewerProps {
	imageList: string[];
	currentIndex?: number;
	handleChangePreview: () => void;
	handleChangeNext: () => void;
}

export default function ImageViewer({
	imageList,
	currentIndex = 0,
	handleChangePreview,
	handleChangeNext
}: ImageViewerProps) {
	const [imageLoaded, setImageLoaded] = useState(false);

	const transformWrapperRef = useRef<ReactZoomPanPinchRef | null>(null);

	const imageSrc = useMemo(() => {
		return imageList[currentIndex];
	}, [currentIndex]);

	const handleChangeImage = (typeChange: "next" | "prev") => {
		typeChange === "next" ? handleChangeNext() : handleChangePreview();
		setImageLoaded(false);
	};

	// on image loaded, reset position and scale
	useEffect(() => {
		if (imageLoaded) transformWrapperRef.current?.centerView(1, 0);
	}, [imageLoaded]);

	return (
		<>
			<Helmet>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
				/>
			</Helmet>
			<FileViewerMobileWrap>
				{currentIndex > 0 && (
					<WrapperBtnChangeImg css={{ left: DISTANCE_TO_SCREEN_EDGE }}>
						<Button.Icon
							filled="neutral"
							name="Left24"
							size="Xlarge"
							onClick={() => handleChangeImage("prev")}
						/>
					</WrapperBtnChangeImg>
				)}
				{currentIndex < imageList.length - 1 && (
					<WrapperBtnChangeImg css={{ right: DISTANCE_TO_SCREEN_EDGE }}>
						<Button.Icon
							filled="neutral"
							name="Right24"
							size="Xlarge"
							onClick={() => handleChangeImage("next")}
						/>
					</WrapperBtnChangeImg>
				)}
				{!imageLoaded && (
					<LoadingFile typoSize={Utility.isMobile ? "Title3_B" : "Head5_B"} typoColor="neutral_00_100">
						{_T("로딩중")}
					</LoadingFile>
				)}
				<TransformWrapper
					ref={transformWrapperRef}
					minScale={MIN_SCALE_MOBILE}
					maxScale={MAX_SCALE_MOBILE}
					wheel={{ step: 1 }}
					pinch={{ disabled: false, step: 0.05 }}
					limitToBounds={true}
					centerOnInit={true}
				>
					<TransformComponent>
						<Image
							loading="lazy"
							onError={(e) => console.error("#ImageViewer#", e)}
							src={imageSrc}
							draggable={false}
							onLoad={() => setImageLoaded(true)}
							css={styleImageViewer}
							errorSrc={image1280x720}
						/>
					</TransformComponent>
				</TransformWrapper>
			</FileViewerMobileWrap>
		</>
	);
}
